import React from 'react';
import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  CountryList,
  CreateObjectPayloadData,
  CreateSocialNetworkType,
  FeedingResponse,
  FeedingUpdateType,
  GetMainObjectsPayload,
  MainObject,
  MainObjectContacts,
  MainObjectList,
  ObjectTypesData,
  ObjectTypesPayload,
  ResponseSocialNetworkType,
  SocialNetworks,
  SocialNetworkType,
  UpdateObjectContactsType,
  UpdateObjectPayload,
} from '../types/mainObjectTypes';
import { PayloadWithCallback } from '../types/@types';

type MainObjectState = {
  mainObjectTypes: ObjectTypesData;
  mainObjectList: MainObjectList | null;
  countriesList: CountryList;
  currentMainObject: MainObject | null;
  feeding: FeedingResponse | null;
  contacts: MainObjectContacts | null;
  socialNetworkTypes: SocialNetworkType[];
  currency: string;
};

const initialState: MainObjectState = {
  mainObjectTypes: {
    fullData: [],
    optionsList: [],
  },
  mainObjectList: null,
  countriesList: [
    { countryCode: 'by', countryLabel: 'Беларусь' },
    { countryCode: 'ru', countryLabel: 'Россия' },
    { countryCode: 'ua', countryLabel: 'Украина' },
    { countryCode: 'kz', countryLabel: 'Казахстан' },
    { countryCode: 'am', countryLabel: 'Армения' },
    { countryCode: 'az', countryLabel: 'Азербайджан' },
    { countryCode: 'ge', countryLabel: 'Грузия' },
    { countryCode: 'kg', countryLabel: 'Кыргызстан' },
    { countryCode: 'md', countryLabel: 'Молдова' },
    { countryCode: 'tj', countryLabel: 'Таджикистан' },
    { countryCode: 'uz', countryLabel: 'Узбекистан' },
    { countryCode: 'ee', countryLabel: 'Эстония' },
    { countryCode: 'lv', countryLabel: 'Латвия' },
    { countryCode: 'lt', countryLabel: 'Литва' },
    { countryCode: 'pl', countryLabel: 'Польша' },
  ],
  currentMainObject: null,
  feeding: null,
  contacts: null,
  currency: "BYN",
  socialNetworkTypes: [
    {
      id: 1,
      title: SocialNetworks.FACEBOOK,
    },
    {
      id: 2,
      title: SocialNetworks.INSTAGRAM,
    },
    {
      id: 3,
      title: SocialNetworks.TELEGRAM,
    },
    {
      id: 4,
      title: SocialNetworks.VIBER,
    },
    {
      id: 5,
      title: SocialNetworks.VK,
    },
  ],
};

const MainObjectSlice = createSlice({
  name: 'mainObject',
  initialState,
  reducers: {
    createMainObject(_, __: PayloadAction<CreateObjectPayloadData>) {},
    addMainObjectInList(state, action: PayloadAction<MainObject>) {
      if (state.mainObjectList) {
        state.mainObjectList = [...state.mainObjectList, action.payload];
      }
    },
    getMainObjectTypes(_, __: PayloadAction<ObjectTypesPayload>) {},
    setMainObjectTypes(state, action: PayloadAction<ObjectTypesData>) {
      state.mainObjectTypes = action.payload;
    },
    getMainObjectList(_, __: PayloadAction<GetMainObjectsPayload>) {}, // GetMainObjectsPayloadData
    setMainObjectList(state, action: PayloadAction<MainObjectList>) {
      state.mainObjectList = action.payload;
    },
    getMainObject(_, __: PayloadAction<PayloadWithCallback<string>>) {},
    getMainObjectTabsData(_, __: PayloadAction<PayloadWithCallback<string>>) {},
    setCurrentObject(state, action: PayloadAction<MainObject | null>) {
      state.currentMainObject = action.payload;
    },
    removeMainObject(_, __: PayloadAction<string>) {},
    setIsLoadingMainObjectCard(
      state,
      action: PayloadAction<{ id: string; value: boolean }>,
    ) {
      const { id, value } = action.payload;

      if (state.mainObjectList) {
        state.mainObjectList = state.mainObjectList.map((object) => {
          return object.id === id ? { ...object, isLoading: value } : object;
        });
      }
    },
    removeMainObjectFromList(state, action: PayloadAction<string>) {
      if (state.mainObjectList) {
        state.mainObjectList = state.mainObjectList.filter(
          (object) => object.id !== action.payload,
        );
      }
    },
    updateMainObject(_, __: PayloadAction<UpdateObjectPayload>) {},
    updateMainObjectInList(state, action: PayloadAction<MainObject>) {
      if (state.mainObjectList) {
        state.mainObjectList = state.mainObjectList.map((object) => {
          return object.id === action.payload.id ? action.payload : object;
        });
      }
    },
    getFeedingSettings(_, __: PayloadAction<string>) {},
    setFeedingSettings(state, action: PayloadAction<FeedingResponse>) {
      state.feeding = { ...action.payload };
    },
    updateFeedingSettings(
      _,
      __: PayloadAction<{ mainId: string; data: FeedingUpdateType }>,
    ) {},

    getContacts(_, __: PayloadAction<string>) {},
    setContacts(state, action: PayloadAction<MainObjectContacts>) {
      state.contacts = { ...action.payload };
    },
    updateObjectContacts(
      _,
      __: PayloadAction<{ id: number; data: UpdateObjectContactsType }>,
    ) {},
    getSocialNetworkTypes(_, __) {},
    setSocialNetworkTypes(state, action: PayloadAction<SocialNetworkType[]>) {
      state.socialNetworkTypes = action.payload;
    },
    createSocialNetworkAction(_, __: PayloadAction<CreateSocialNetworkType>) {},
    setSocialNetwork(state, action: PayloadAction<ResponseSocialNetworkType>) {
      const index = state.contacts?.social_networks.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index && index === -1) {
        state.contacts?.social_networks.push(action.payload);
      } else if (
        index &&
        state.contacts?.social_networks &&
        state.contacts?.social_networks?.length > 0 &&
        index !== -1
      ) {
        state.contacts.social_networks = state.contacts?.social_networks.map(
          (item) => (item.id === action.payload.id ? action.payload : item),
        );
      }
    },
    deleteSocialNetworkAction(_, __: PayloadAction<number>) {},
  },
});

export const {
  createMainObject,
  addMainObjectInList,
  getMainObjectTypes,
  setMainObjectTypes,
  getMainObjectList,
  setMainObjectList,
  getMainObject,
  setCurrentObject,
  updateMainObject,
  removeMainObject,
  getMainObjectTabsData,
  setIsLoadingMainObjectCard,
  removeMainObjectFromList,
  updateMainObjectInList,
  getFeedingSettings,
  setFeedingSettings,
  updateFeedingSettings,
  getContacts,
  setContacts,
  updateObjectContacts,
  getSocialNetworkTypes,
  setSocialNetworkTypes,
  createSocialNetworkAction,
} = MainObjectSlice.actions;

export default MainObjectSlice.reducer;

export const mainObjectName = MainObjectSlice.name;

const getCountriesList = (state: RootState) => state.mainObject.countriesList;

const getSortedCountriesList = createSelector([getCountriesList], (list) => {
  const listCopy = [...list];
  return listCopy.sort((a, b: any) =>
    a.countryLabel.localeCompare(b.countryLabel),
  );
});

const getCountriesOptions = createSelector([getCountriesList], (list) => {
  return list.map((country) => ({
    value: country.countryCode,
    label: country.countryLabel,
  }));
});

export const MainObjectSelectors = {
  getMainObjectTypesData: (state: RootState) =>
    state.mainObject.mainObjectTypes,
  getMainObjectList: (state: RootState) => state.mainObject.mainObjectList,
  getCurrentObject: (state: RootState) => state.mainObject.currentMainObject,
  getFeedingSetting: (state: RootState) => state.mainObject.feeding,
  getMainObjectContacts: (state: RootState) => state.mainObject.contacts,
  getSocialNetworksTypes: (state: RootState) =>
    state.mainObject.socialNetworkTypes,
  getCountriesList,
  getSortedCountriesList,
  getCountriesOptions,
};
